import React from 'react';

import Button from './Button';
import { ButtonPropTypes } from './buttonPropsTypes';
import injectStores from '../RootStore/injectStores';
import type { RouterStore } from 'stores/RouterStore';

interface DialogButtonProps extends ButtonPropTypes {
  dialogName: string;
  dialogTab?: string;
  dialogContext?: string;
  routerStore?: RouterStore;
}
/**
 * `DialogButton` is a React functional component that renders a button
 * which, when clicked, opens a dialog using the provided `routerStore`.
 *
 * @component
 * @param {DialogButtonProps} props - The properties for the DialogButton component.
 * @param {string} props.dialogName - The name of the dialog to be opened.
 * @param {string} [props.dialogTab] - The optional tab of the dialog to be opened.
 * @param {string} [props.dialogContext] - The optional context to be passed to the dialog.
 * @param {RouterStore} props.routerStore - The store responsible for handling dialog operations.
 * @param {React.ReactNode} props.children - The content to be displayed inside the button.
 * @param {object} [props.buttonProps] - Additional properties to be passed to the Button component.
 *
 * @example
 * <DialogButton
 *   dialogName="exampleDialog"
 *   dialogTab="settings"
 *   dialogContext={{ userId: 123 }}
 * >
 *   Open Dialog
 * </DialogButton>
 */
const DialogButton = injectStores(
  ({
    dialogName,
    dialogTab,
    dialogContext,
    routerStore,
    children,
    ...buttonProps
  }: DialogButtonProps) => {
    const handleClick = () => {
      if (routerStore) {
        routerStore.openDialog({
          dialogName: dialogName,
          dialogTab: dialogTab || undefined,
          dialogContext: dialogContext || undefined,
        });
      }
    };

    return (
      <Button onClick={handleClick} {...buttonProps}>
        {children}
      </Button>
    );
  }
);

export default DialogButton;
